import { parse, isValid, format } from 'date-fns';

/**
 * Attempts to parse a date string in various common formats
 * Returns YYYY-MM-DD format or today's date if parsing fails
 */
export function parseFlexibleDate(dateStr: string): string {
  if (!dateStr || typeof dateStr !== 'string') {
    return format(new Date(), 'yyyy-MM-dd');
  }

  const cleaned = dateStr.trim();
  
  // If already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
    return cleaned;
  }

  // Common date formats to try
  const formats = [
    'yyyy-MM-dd',
    'dd/MM/yyyy',
    'MM/dd/yyyy',
    'dd-MM-yyyy',
    'MM-dd-yyyy',
    'yyyy/MM/dd',
    'dd.MM.yyyy',
    'MM.dd.yyyy',
    'yyyyMMdd',
    'd/M/yyyy',
    'M/d/yyyy',
    'd-M-yyyy',
    'M-d-yyyy',
    'dd MMM yyyy',
    'MMM dd, yyyy',
    'MMMM dd, yyyy',
    'dd MMMM yyyy',
  ];

  for (const fmt of formats) {
    try {
      const parsed = parse(cleaned, fmt, new Date());
      if (isValid(parsed) && parsed.getFullYear() > 1900 && parsed.getFullYear() < 2100) {
        return format(parsed, 'yyyy-MM-dd');
      }
    } catch {
      // Try next format
    }
  }

  // Try native Date parsing as fallback
  try {
    const parsed = new Date(cleaned);
    if (isValid(parsed) && parsed.getFullYear() > 1900 && parsed.getFullYear() < 2100) {
      return format(parsed, 'yyyy-MM-dd');
    }
  } catch {
    // Fallback to today
  }

  // Return today's date if all parsing fails
  console.warn(`Could not parse date: "${dateStr}", using today's date`);
  return format(new Date(), 'yyyy-MM-dd');
}

/**
 * Validates that a date string is in YYYY-MM-DD format
 */
export function isValidDateFormat(dateStr: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
}

/**
 * Parse Tally date format (YYYYMMDD) to YYYY-MM-DD
 */
export function parseTallyDate(dateStr: string): string {
  if (!dateStr || dateStr.length !== 8) {
    return format(new Date(), 'yyyy-MM-dd');
  }
  
  const year = dateStr.slice(0, 4);
  const month = dateStr.slice(4, 6);
  const day = dateStr.slice(6, 8);
  
  const parsed = new Date(`${year}-${month}-${day}`);
  if (isValid(parsed) && parsed.getFullYear() > 1900 && parsed.getFullYear() < 2100) {
    return `${year}-${month}-${day}`;
  }
  
  return format(new Date(), 'yyyy-MM-dd');
}
